import React from 'react';
import { Tooltip } from '@arco-design/web-react';
import {
	AdvancedType,
	BasicType,
	DynamicType,
	getAncestorCustomListingIdx,
	isListingScopeBlock,
	getParentIdx,
	getSiblingIdx,
	isRootChildIdx,
	t,
} from '@bay-email/core';
import {
	useBlock,
	useEditorContext,
	useFocusIdx,
	useEditorProps,
	useFocusBlockLayout,
	BlockAvatarWrapper,
} from '@bay-email/editor';
import { get } from 'lodash';
import { useAddToCollection } from '@extensions/hooks/useAddToCollection';
import { getBlockTitle } from '@extensions/utils/getBlockTitle';
import { useExtensionProps } from '@extensions/components/Providers/ExtensionProvider';
import {
	collectAdvancedTableCellContent,
	dispatchOpenAttachedListingPicker,
	supportsAttachedListing,
	usesListingMergeTags,
} from '@extensions/AttributePanel/components/blocks/shared/listingAttachment';
import {
	BsArrow90DegUp,
	BsCollection,
	BsCopy,
	BsGripVertical,
	BsHouseDoor,
	BsTrash,
} from 'react-icons/bs';

const ACTION_ICON_SIZE = '1.15rem';
const TOOLBAR_HEIGHT = 27;

// Shared chrome for the toolbar's solid pieces (title tab + action buttons): white text
// on the selection colour, fixed height, no wrapping.
const TAB_BASE_STYLE: React.CSSProperties = {
	color: '#ffffff',
	backgroundColor: 'var(--selected-color)',
	height: TOOLBAR_HEIGHT,
	boxSizing: 'border-box',
	whiteSpace: 'nowrap',
	maxWidth: 300,
	overflow: 'hidden',
};

export function Toolbar() {
	const { moveBlock, copyBlock, removeBlock, focusBlock } = useBlock();
	const { focusIdx, setFocusIdx } = useFocusIdx();
	const { focusBlockNode } = useFocusBlockLayout();
	const {
		formState: { values },
	} = useEditorContext();
	const { modal, setModalVisible } = useAddToCollection();
	const props = useEditorProps();
	const { isDesignAdmin } = useExtensionProps();

	const isPage = focusBlock?.type === BasicType.PAGE;

	// Block editor variant: every direct child of the Page is a root Wrapper — it can't
	// be duplicated or escaped upward (that would reach the hidden Page).
	const { blockMode } = props;
	const isBlockRootWrapper = Boolean(blockMode) && isRootChildIdx(focusIdx);

	// The tab normally sits ABOVE the block (translateY(-100%)). When the block is at
	// the top of a clipping container (e.g. the top of a column/section, or scrolled to
	// the top of the canvas), that space is clipped and the tab disappears. In that case
	// flip it to sit at the block's top edge instead so it stays visible.
	const placeTabInside = React.useMemo(() => {
		if (!focusBlockNode) return false;
		const blockTop = focusBlockNode.getBoundingClientRect().top;
		let el: HTMLElement | null = focusBlockNode.parentElement;
		while (el) {
			const styles = getComputedStyle(el);
			const overflow = `${styles.overflow}${styles.overflowY}${styles.overflowX}`;
			if (/(hidden|auto|scroll|clip)/.test(overflow)) {
				// Not enough room above the block within this clipping ancestor.
				return blockTop - el.getBoundingClientRect().top < TOOLBAR_HEIGHT + 2;
			}
			el = el.parentElement;
		}
		// Nothing clips above the block, so the tab won't be hidden.
		return false;
	}, [focusBlockNode]);
	// Dedicated listing blocks own their own listing selection; everything else
	// (text/image/button/table…) defers to the Listing Wrapper — or the column
	// switched to "Listing" — it sits in. When inside one, the Select Listing action targets that block so a
	// single pick hydrates every field in it (upward traversal).
	const ownsListingSelection =
		focusBlock?.type === 'text_single_listing' ||
		focusBlock?.type === 'listings_block' ||
		focusBlock?.type === 'criteria_block' ||
		isListingScopeBlock(focusBlock);
	const customListingIdx = React.useMemo(
		() =>
			ownsListingSelection
				? null
				: getAncestorCustomListingIdx((values as any)?.content, focusIdx),
		[ownsListingSelection, values, focusIdx],
	);
	// A child sitting inside a Listing Wrapper (or listing column) has no content of its own
	// yet to signal it needs the icon (no merge tags, nothing picked) — so for any
	// block type that's otherwise eligible, being scoped is reason enough on its
	// own to surface the house icon; that's the only way to reach the drawer that
	// sets the listing on the ancestor.
	const insideListingScope = Boolean(customListingIdx);
	const showAttachedListingAction = React.useMemo(() => {
		const blockType = focusBlock?.type;
		if (!blockType || !supportsAttachedListing(blockType)) return false;

		if (insideListingScope) return true;

		const hasAttachedListingSelection = Boolean(
			focusBlock?.data?.value?.selectedListingId ||
			focusBlock?.data?.value?.selectedListing,
		);

		const isTextBlock = blockType === BasicType.TEXT || blockType === AdvancedType.TEXT;
		if (isTextBlock) {
			const contentValue = String(focusBlock?.data?.value?.content || '');
			return hasAttachedListingSelection || usesListingMergeTags(contentValue);
		}

		const isButtonBlock =
			blockType === BasicType.BUTTON || blockType === AdvancedType.BUTTON;
		if (isButtonBlock) {
			const hrefValue = String(focusBlock?.attributes?.href || '');
			return hasAttachedListingSelection || usesListingMergeTags(hrefValue);
		}

		const isTableBlock =
			blockType === BasicType.TABLE || blockType === AdvancedType.TABLE;
		if (isTableBlock) {
			// Basic tables store one HTML string in data.value.content; advanced tables
			// store per-cell HTML in tableSource, so gather every cell's content too.
			const contentValue =
				String(focusBlock?.data?.value?.content || '') +
				' ' +
				collectAdvancedTableCellContent(focusBlock);
			return hasAttachedListingSelection || usesListingMergeTags(contentValue);
		}

		const isImageBlock =
			blockType === BasicType.IMAGE ||
			blockType === AdvancedType.IMAGE ||
			blockType === DynamicType.IMAGE;

		if (isImageBlock) {
			const imageType = focusBlock?.data?.value?.imageType;
			const dynamicImageTag = String(
				focusBlock?.data?.value?.dynamicImageTag || '',
			).trim();
			const srcValue = String(focusBlock?.attributes?.src || '');
			const hrefValue = String(focusBlock?.attributes?.href || '');
			const isDynamicImageMode =
				blockType === DynamicType.IMAGE ||
				imageType === 'dynamic' ||
				(imageType !== 'static' && Boolean(dynamicImageTag));
			const staticModeHasListingMergeTags =
				usesListingMergeTags(srcValue) || usesListingMergeTags(hrefValue);
			const dynamicModeHasListingMergeTags =
				usesListingMergeTags(dynamicImageTag) ||
				usesListingMergeTags(srcValue) ||
				usesListingMergeTags(hrefValue);

			const hasListingMergeTags = isDynamicImageMode
				? dynamicModeHasListingMergeTags
				: staticModeHasListingMergeTags;

			return hasAttachedListingSelection || hasListingMergeTags;
		}

		// Section/Column/dedicated listing blocks: type eligibility alone is enough.
		return true;
	}, [
		focusBlock?.attributes?.href,
		focusBlock?.attributes?.src,
		focusBlock?.data?.value?.content,
		focusBlock?.data?.value?.dynamicImageTag,
		focusBlock?.data?.value?.imageType,
		focusBlock?.data?.value?.selectedListing,
		focusBlock?.data?.value?.selectedListingId,
		focusBlock?.data?.value?.tableSource,
		focusBlock?.type,
		insideListingScope,
	]);
	const isColumnBlock =
		focusBlock?.type === BasicType.COLUMN || focusBlock?.type === AdvancedType.COLUMN;
	const parentIdx = React.useMemo(() => getParentIdx(focusIdx), [focusIdx]);
	const parentBlock = React.useMemo(() => {
		if (!parentIdx) return null;
		return get(values, parentIdx);
	}, [parentIdx, values]);
	const parentSectionHasListing = Boolean(parentBlock?.data?.value?.selectedListingId);
	const isColumnListingActionDisabled = isColumnBlock && parentSectionHasListing;
	const listingActionTitle = isColumnListingActionDisabled
		? t('Section already has a listing selected. Clear Section listing first.')
		: t('Select Listing');

	const handleMoveUp = () => {
		moveBlock(focusIdx, getSiblingIdx(focusIdx, -1));
	};

	const handleMoveDown = () => {
		moveBlock(focusIdx, getSiblingIdx(focusIdx, 1));
	};

	const handleAddToCollection = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
		setModalVisible(true);
	};

	const handleCopy: React.MouseEventHandler<HTMLDivElement> = ev => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
		copyBlock(focusIdx);
	};

	const handleDelete = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
		removeBlock(focusIdx);
	};

	const handleSelectParent = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
		setFocusIdx(getParentIdx(focusIdx)!);
	};

	const handleOpenListingPicker = () => {
		if (isColumnListingActionDisabled) return;
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
		// Retarget to the container (and focus it so its panel mounts) when the
		// clicked block lives inside one; otherwise open the block's own picker.
		const targetIdx = customListingIdx || focusIdx;
		if (targetIdx !== focusIdx) {
			setFocusIdx(targetIdx);
		}
		dispatchOpenAttachedListingPicker(targetIdx);
	};

	return (
		<>
			<div
				id='bay-email-extensions-InteractivePrompt-Toolbar'
				style={{ height: 0, zIndex: 100 }}
			>
				<div
					style={{
						fontFamily: 'Inter, sans-serif',
						fontSize: 15,
						lineHeight: `${TOOLBAR_HEIGHT}px`,
						pointerEvents: 'auto',
						color: '#ffffff',
						// Sit above the block normally; flip to the block's top edge when there
						// isn't room above (block at the top of a column/section/canvas).
						transform: placeTabInside ? 'translateY(0)' : 'translateY(-100%)',
						display: 'inline-flex',
					}}
				>
					{!isPage && focusBlock ? (
						// The whole title tab (grip + block title) is the drag handle.
						<BlockAvatarWrapper
							idx={focusIdx}
							type={focusBlock.type}
							action='move'
						>
							<div
								title={t('Drag to move')}
								style={
									{
										...TAB_BASE_STYLE,
										display: 'flex',
										alignItems: 'center',
										gap: 4,
										paddingLeft: 2,
										paddingRight: 8,
										lineHeight: 1.25,
										cursor: 'grab',
										pointerEvents: 'auto',
										userSelect: 'none',
										WebkitUserDrag: 'element',
									} as any
								}
							>
								<BsGripVertical
									size={ACTION_ICON_SIZE}
									style={{ flexShrink: 0, cursor: 'grab' }}
								/>
								<span
									style={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										cursor: 'grab',
									}}
								>
									{getBlockTitle(focusBlock, false)}
								</span>
							</div>
						</BlockAvatarWrapper>
					) : (
						<div style={{ ...TAB_BASE_STYLE, display: 'inline-flex', padding: '1px 8px' }}>
							{focusBlock && getBlockTitle(focusBlock, false)}
						</div>
					)}
					<div
						onClick={e => {
							e.stopPropagation();
						}}
						onMouseDown={ev => {
							ev.preventDefault();
						}}
						style={{
							display: isPage ? 'none' : 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							pointerEvents: 'auto',
						}}
					>
						{!isBlockRootWrapper && (
							<ToolItem
								title={t('View Upper Layer')}
								icon={<BsArrow90DegUp size={ACTION_ICON_SIZE} />}
								onClick={handleSelectParent}
							/>
						)}
						{!isBlockRootWrapper && (
							<ToolItem
								title={t('Duplicate Block')}
								icon={<BsCopy size={ACTION_ICON_SIZE} />}
								onClick={handleCopy}
							/>
						)}
						{/* Hide the Select Listing action in Design Manager — it stays in the
						   attribute drawer, but not on the block's action toolbar. */}
						{showAttachedListingAction && !isDesignAdmin && (
							<ToolItem
								title={listingActionTitle}
								icon={<BsHouseDoor size={ACTION_ICON_SIZE} />}
								disabled={isColumnListingActionDisabled}
								onClick={handleOpenListingPicker}
							/>
						)}
						{props.onAddCollection && (
							<ToolItem
								title={t('Add to collection')}
								icon={<BsCollection size={ACTION_ICON_SIZE} />}
								onClick={handleAddToCollection}
							/>
						)}
						{/* Delete stays available on the root wrapper in block mode: the
						   block's single wrapper is deletable so the builder can swap a
						   plain Wrapper for a Custom Listing Wrapper (and re-add from the
						   sidebar). Duplicate/View-Upper-Layer stay hidden — a copy would
						   make a second root wrapper, bypassing the drop guard. */}
						<ToolItem
							title={t('Delete Block')}
							icon={<BsTrash size={ACTION_ICON_SIZE} />}
							onClick={handleDelete}
						/>
					</div>
				</div>
			</div>
			{modal}
		</>
	);
}

function ToolItem(props: {
	icon: React.ReactNode;
	onClick: React.MouseEventHandler<HTMLDivElement>;
	title: string;
	disabled?: boolean;
}) {
	return (
		<Tooltip content={props.title}>
			<div
				onClick={props.disabled ? undefined : props.onClick}
				aria-label={props.title}
				style={{
					color: props.disabled ? 'rgba(255, 255, 255, 0.6)' : '#ffffff',
					backgroundColor: 'var(--selected-color)',
					height: TOOLBAR_HEIGHT,
					lineHeight: `${TOOLBAR_HEIGHT}px`,
					width: 28,
					display: 'flex',
					alignItems: 'center',
					// White divider before each action — separates the actions from the title
					// tab and from each other, consistent across browsers and DPRs.
					borderLeft: '1px solid #ffffff',
					boxSizing: 'border-box',
					pointerEvents: 'auto',
					cursor: props.disabled ? 'not-allowed' : 'pointer',
					justifyContent: 'center',
				}}
			>
				<span
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
						cursor: 'pointer',
						pointerEvents: 'none',
					}}
				>
					{props.icon}
				</span>
			</div>
		</Tooltip>
	);
}
